import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useQuote from '../hooks/useQuote';
import QuoteValidityBadge from './QuoteValidityBadge';
import { Pencil, Clock } from 'lucide-react';
import { BRAND } from '../brand';

// URLs de banderas de flagcdn.com con fallback
const flagUrls = {
  CL: 'https://flagcdn.com/w80/cl.png',
  BO: 'https://flagcdn.com/w80/bo.png',
  AR: 'https://flagcdn.com/w80/ar.png',
  PE: 'https://flagcdn.com/w80/pe.png',
  VE: 'https://flagcdn.com/w80/ve.png',
  CO: 'https://flagcdn.com/w80/co.png',
};
const flagEmojis = { CL: '🇨🇱', BO: '🇧🇴', AR: '🇦🇷', PE: '🇵🇪', VE: '🇻🇪', CO: '🇨🇴' };

const FlagImg = ({ country, size = 20 }) => {
  const [imgError, setImgError] = useState(false);
  if (imgError || !flagUrls[country]) {
    return (
      <span
        style={{ fontSize: size * 0.9, display: 'inline-block', verticalAlign: 'middle', lineHeight: 1 }}
        role="img"
        aria-label={`Bandera ${country}`}
      >
        {flagEmojis[country] || country}
      </span>
    );
  }
  return (
    <img
      src={flagUrls[country]}
      alt={`Bandera ${country}`}
      style={{ width: size, height: 'auto', display: 'inline-block', verticalAlign: 'middle', borderRadius: '2px' }}
      onError={() => setImgError(true)}
      loading="eager"
    />
  );
};

// Config brand-aware. Fail-closed si falta primary/corridors/origins.
const brandExchange = BRAND?.exchange || null;
const brandPrimary = brandExchange?.primary || null;
const brandCorridors = Array.isArray(brandExchange?.corridors) ? brandExchange.corridors : [];
const brandOrigins = Array.isArray(brandExchange?.origins) ? brandExchange.origins : [];
const activeDestCorridors = brandCorridors.filter((c) => c && c.active && c.currency);

const CalculatorLuxury = ({ onSendMoney, hideCta = false, ctaLabel, headerFlags = false } = {}) => {
  const navigate = useNavigate();

  const origins = brandOrigins;
  const defaultOrigin = origins[0] || null;
  const defaultDestCurrency = brandPrimary?.currency || activeDestCorridors[0]?.currency || null;
  const configReady = Boolean(
    defaultOrigin &&
    defaultDestCurrency &&
    activeDestCorridors.length > 0 &&
    brandPrimary?.currency,
  );

  const layout = BRAND?.landing?.calculator?.layout || 'classic';

  const [originCurrency, setOriginCurrency] = useState(defaultOrigin?.code || 'CLP');
  const [amountFrom, setAmountFrom] = useState(
    String(defaultOrigin?.code === 'CLP' ? 100000 : (defaultOrigin?.minTransfer || 0)),
  );
  const [displayFrom, setDisplayFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [displayTo, setDisplayTo] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(defaultDestCurrency || '');
  const [corridorsCfg, setCorridorsCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastEditedRef = useRef('from');
  // Cuál input está activo — para NO reformatear su texto mientras el
  // cliente escribe. Cuando la respuesta llega por el otro campo, sí se
  // reformatea. Cuando el usuario deja de enfocar, se reformatea también.
  const [focusedField, setFocusedField] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Traer flags de corredores del backend (permite deshabilitar corredores por instancia).
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/config/corridors`)
      .then((r) => setCorridorsCfg(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  const { quote, calculateForward, calculateReverse, reset: resetQuote } = useQuote();

  const formatNumber = (num, { decimals = 0 } = {}) => {
    if (num === '' || num === null || num === undefined) return '';
    const raw = num.toString().replace(',', '.');
    const parts = raw.split('.');
    const intPart = parts[0].replace(/\./g, '');
    const number = parseInt(intPart, 10);
    if (isNaN(number)) return '';
    let decPart = '';
    if (parts.length === 2 && parts[1].length > 0) {
      decPart = parts[1].slice(0, Math.max(0, decimals));
    }
    // Si el valor original ya trae decimales explícitos, respetarlos;
    // si no, no forzamos decimales para no cambiar longitud mientras se tipea.
    return number.toLocaleString('es-CL', { timeZone: 'America/Santiago' }) + (decPart ? ',' + decPart : '');
  };
  const unformatNumber = (str) => (str ? String(str).replace(/\./g, '').replace(',', '.') : '');

  // Info del origen actual.
  const originInfo = useMemo(() => {
    return origins.find((o) => o.code === originCurrency) || defaultOrigin;
  }, [originCurrency, origins, defaultOrigin]);

  // Destinos ofrecidos según origen actual + gating por corridorsCfg del backend.
  const destinationOptions = useMemo(() => {
    if (!originInfo || !configReady) return [];
    let base = [];
    if (originInfo.code === 'CLP') {
      base = activeDestCorridors.map((c) => ({
        code: c.currency,
        name: c.name,
        flagCode: c.flagCode || c.code,
        symbol: c.symbol,
      }));
    } else {
      const chile = { code: 'CLP', name: 'Chile', flagCode: 'CL', symbol: '$' };
      const others = activeDestCorridors
        .filter((c) => c.currency !== originInfo.code)
        .map((c) => ({ code: c.currency, name: c.name, flagCode: c.flagCode || c.code, symbol: c.symbol }));
      base = [chile, ...others];
    }
    // Filtrar por corridorsCfg si el backend deshabilitó explícitamente el par.
    if (corridorsCfg?.corridors) {
      base = base.filter(
        (d) => corridorsCfg.corridors[`${originInfo.code}-${d.code}`] !== false,
      );
    }
    return base;
  }, [originInfo, configReady, corridorsCfg]);

  // Reconciliar destino seleccionado si dejó de ser válido.
  useEffect(() => {
    if (!destinationOptions.length) return;
    if (!destinationOptions.some((d) => d.code === selectedCountry)) {
      const fallback = originInfo?.code === defaultDestCurrency
        ? 'CLP'
        : (destinationOptions.find((d) => d.code === defaultDestCurrency)?.code || destinationOptions[0].code);
      setSelectedCountry(fallback);
    }
    // eslint-disable-next-line
  }, [destinationOptions.map((d) => d.code).join(',')]);

  // Reset amounts al cambiar origen.
  const previousOriginRef = useRef(originCurrency);
  useEffect(() => {
    if (previousOriginRef.current === originCurrency) return;
    previousOriginRef.current = originCurrency;
    const seed = originInfo?.code === 'CLP' ? 100000 : (originInfo?.minTransfer || 0);
    const seedStr = String(seed);
    setAmountFrom(seedStr);
    setDisplayFrom(formatNumber(seedStr));
    setAmountTo('');
    setDisplayTo('');
    resetQuote();
  }, [originCurrency, originInfo, resetQuote]);

  // Inicializar displayFrom al arrancar.
  useEffect(() => {
    if (!displayFrom && amountFrom) {
      setDisplayFrom(formatNumber(amountFrom));
    }
    // eslint-disable-next-line
  }, []);

  // Recalcular al cambiar par o al terminar de cargar config.
  useEffect(() => {
    if (!configReady || loading) return;
    if (lastEditedRef.current === 'to' && amountTo) {
      calculateReverse(amountTo, originCurrency, selectedCountry);
    } else if (amountFrom) {
      calculateForward(amountFrom, originCurrency, selectedCountry);
    }
    // eslint-disable-next-line
  }, [originCurrency, selectedCountry, configReady, loading]);

  // Sincronizar hook con inputs.
  // Regla: NO reformatear el campo que el cliente está editando activamente.
  // El otro campo sí se actualiza con el resultado del backend.
  useEffect(() => {
    if (!quote) return;
    if (quote.direction === 'forward') {
      const result = quote.amount_destination ?? 0;
      setAmountTo(String(result));
      if (focusedField !== 'to') {
        // Con rounding=1 el backend puede devolver decimales (ej: 999,99 PEN).
        // Mostramos hasta 2 decimales si existen.
        const hasDecimals = result % 1 !== 0;
        setDisplayTo(formatNumber(result, { decimals: hasDecimals ? 2 : 0 }));
      }
    } else if (quote.direction === 'reverse') {
      const result = quote.amount_origin ?? 0;
      setAmountFrom(String(result));
      if (focusedField !== 'from') {
        const hasDecimals = result % 1 !== 0;
        setDisplayFrom(formatNumber(result, { decimals: hasDecimals ? 2 : 0 }));
      }
    }
    // eslint-disable-next-line
  }, [quote]);

  const handleFromChange = (value) => {
    // Permitir dígitos y una coma/punto decimal (por si el usuario escribe centavos).
    const cleanValue = value.replace(/[^\d.,]/g, '');
    const numericValue = unformatNumber(cleanValue);
    lastEditedRef.current = 'from';
    setAmountFrom(numericValue);
    // Mientras el usuario escribe, NO reformateamos el string tipeado: se mantiene
    // tal cual (sólo removemos caracteres inválidos y reformateamos los miles en
    // la parte entera para lectura). Los decimales se preservan literalmente.
    const parts = cleanValue.replace(/\./g, '').replace(',', '.').split('.');
    const intFormatted = parts[0] ? parseInt(parts[0], 10).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '';
    const displayValue = parts.length === 2 ? `${intFormatted},${parts[1]}` : intFormatted;
    setDisplayFrom(displayValue);
    if (!numericValue || parseFloat(numericValue) === 0) {
      setAmountTo('');
      setDisplayTo('');
      resetQuote();
      return;
    }
    calculateForward(numericValue, originCurrency, selectedCountry);
  };

  const handleToChange = (value) => {
    const cleanValue = value.replace(/[^\d.,]/g, '');
    const numericValue = unformatNumber(cleanValue);
    lastEditedRef.current = 'to';
    setAmountTo(numericValue);
    const parts = cleanValue.replace(/\./g, '').replace(',', '.').split('.');
    const intFormatted = parts[0] ? parseInt(parts[0], 10).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '';
    const displayValue = parts.length === 2 ? `${intFormatted},${parts[1]}` : intFormatted;
    setDisplayTo(displayValue);
    if (!numericValue || parseFloat(numericValue) === 0) {
      setAmountFrom('');
      setDisplayFrom('');
      resetQuote();
      return;
    }
    calculateReverse(numericValue, originCurrency, selectedCountry);
  };

  const selectedDest = useMemo(
    () => destinationOptions.find((d) => d.code === selectedCountry) || null,
    [destinationOptions, selectedCountry],
  );

  const effectiveRateRaw = useMemo(() => {
    const a = parseFloat(unformatNumber(displayFrom || amountFrom));
    const b = parseFloat(unformatNumber(displayTo || amountTo));
    if (!a || !b) return null;
    return b / a;
  }, [displayFrom, displayTo, amountFrom, amountTo]);
  const effectiveRateStr = effectiveRateRaw
    ? (effectiveRateRaw < 1
        ? effectiveRateRaw.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
        : effectiveRateRaw.toFixed(4).replace(/0+$/, '').replace(/\.$/, ''))
    : null;

  // Validación de min/max — fuente única `originInfo.minTransfer/maxTransfer`,
  // aplicada tanto en directa como en inversa. El mensaje se muestra en la
  // moneda que corresponde según el sentido de la validación.
  const rangeValidation = useMemo(() => {
    const min = originInfo?.minTransfer;
    const max = originInfo?.maxTransfer;
    const symbol = originInfo?.symbol || '';
    const code = originInfo?.code || '';
    const fromNum = parseFloat(amountFrom) || 0;
    if (!fromNum || !originInfo) return null;
    if (min && fromNum < min) {
      return {
        level: 'error',
        message: `Monto mínimo: ${symbol}${min.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${code}${
          amountTo ? ` (equivalente a ~${parseFloat(amountTo).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${selectedCountry})` : ''
        }`,
      };
    }
    if (max && fromNum > max) {
      return {
        level: 'error',
        message: `Monto máximo: ${symbol}${max.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${code}${
          amountTo ? ` (equivalente a ~${parseFloat(amountTo).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${selectedCountry})` : ''
        }`,
      };
    }
    return null;
  }, [amountFrom, amountTo, originInfo, selectedCountry]);

  // Aviso de ajuste destino ↔ pedido (nuevo backend enriquece con
  // destination_adjusted / destination_diff). Con rounding=1 casi siempre es
  // false, pero es la red de seguridad honesta.
  const adjustmentNotice = useMemo(() => {
    if (!quote || quote.direction !== 'reverse') return null;
    if (!quote.destination_adjusted) return null;
    const target = quote.target_destination_requested;
    const real = quote.amount_destination;
    if (target == null || real == null) return null;
    return `Recibirá ${selectedDest?.symbol || ''} ${real.toLocaleString('es-CL', { maximumFractionDigits: 2, timeZone: 'America/Santiago' })} (ajustado desde ${selectedDest?.symbol || ''} ${Number(target).toLocaleString('es-CL', { maximumFractionDigits: 2, timeZone: 'America/Santiago' })})`;
  }, [quote, selectedDest]);

  const handleCta = () => {
    if (typeof onSendMoney === 'function') onSendMoney();
    else navigate('/register');
  };

  const brandTheme = BRAND.theme || {};
  const brandPrimaryColor = brandTheme.primary;
  const brandAccentColor = brandTheme.accent;

  // Fail-closed universal.
  if (!configReady) {
    return (
      <div
        className="bg-white rounded-2xl p-6 border-2 border-red-200"
        data-testid="calculator-luxury"
      >
        <h3 className="text-lg font-bold text-red-700 mb-2" data-testid="calc-config-error">
          Calculadora no disponible
        </h3>
        <p className="text-sm text-gray-600">
          La configuración de corredores de {BRAND.name} está incompleta. Contacta a soporte para habilitar
          las cotizaciones.
        </p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // LAYOUT SHOWCASE (EnviosClick y variantes con calculator.layout='showcase').
  // ═══════════════════════════════════════════════════════════════════════
  if (layout === 'showcase') {
    const showOriginToggle = origins.length > 1;
    return (
      <div className="bg-white rounded-[32px] p-7 sm:p-9" data-testid="calculator-luxury">
        {headerFlags ? (
          <h3
            className="text-[22px] sm:text-[26px] font-black text-gray-900 mb-7 flex items-center gap-2 flex-wrap"
            style={{ letterSpacing: '-0.02em' }}
            data-testid="calc-header-flags"
          >
            <FlagImg country={originInfo?.flagCode || 'CL'} size={26} />
            <span>{originInfo?.name || ''}</span>
            <span className="text-gray-400">→</span>
            <FlagImg country={selectedDest?.flagCode || ''} size={26} />
            <span>{selectedDest?.name || ''}</span>
          </h3>
        ) : (
          <h3
            className="text-[22px] sm:text-[26px] font-black text-gray-900 mb-7"
            style={{ letterSpacing: '-0.02em' }}
          >
            Calcula tu envío
          </h3>
        )}

        {showOriginToggle && (
          <div className="mb-6">
            <label className="block text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2.5">
              Envías desde
            </label>
            <div className="flex gap-2">
              {origins.map((o) => (
                <button
                  key={o.code}
                  onClick={() => setOriginCurrency(o.code)}
                  className="flex-1 py-3 px-3 rounded-2xl flex items-center justify-center gap-2 transition text-sm font-bold"
                  style={
                    originCurrency === o.code
                      ? { background: `${brandPrimaryColor}12`, border: `2px solid ${brandPrimaryColor}`, color: brandPrimaryColor }
                      : { background: '#F5F4F7', border: '2px solid transparent', color: '#4b5563' }
                  }
                  data-testid={`origin-${o.code.toLowerCase()}`}
                >
                  <FlagImg country={o.flagCode} size={22} />
                  {o.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tarjeta única blanca: dos campos separados por una línea, en la
            misma pareja visual. Elimina el bloque morado; jerarquía por
            tamaño y color, no por fondo. */}
        <div
          className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_10px_30px_-15px_rgba(0,0,0,0.08)] overflow-hidden mb-4"
          data-testid="calc-card"
        >
          {/* ─── FILA "TÚ ENVÍAS" ─── */}
          <div className="px-5 sm:px-6 py-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                Tú envías
              </span>
              <div
                className="flex items-center gap-1.5 shrink-0 rounded-full bg-gray-100 pl-2.5 pr-3 py-1"
                data-testid="calc-from-currency-indicator"
              >
                <FlagImg country={originInfo?.flagCode} size={18} />
                <span className="text-[11px] font-bold text-gray-700">{originInfo?.code}</span>
              </div>
            </div>
            <label
              className="flex items-center gap-2 rounded-xl px-3 py-2 cursor-text transition-colors"
              style={{
                backgroundColor: '#F8F7FB',
                border: focusedField === 'from'
                  ? `1.5px solid ${brandPrimaryColor}`
                  : '1px solid #E9E7EE',
              }}
              onMouseEnter={(e) => {
                if (focusedField !== 'from') e.currentTarget.style.borderColor = `${brandPrimaryColor}55`;
              }}
              onMouseLeave={(e) => {
                if (focusedField !== 'from') e.currentTarget.style.borderColor = '#E9E7EE';
              }}
            >
              <span
                className="text-[24px] sm:text-[30px] font-black shrink-0"
                style={{ color: brandPrimaryColor }}
              >
                {originInfo?.symbol || ''}
              </span>
              <input
                type="text"
                value={displayFrom}
                onChange={(e) => handleFromChange(e.target.value)}
                onFocus={(e) => { setFocusedField('from'); e.target.select(); }}
                onBlur={() => {
                  setFocusedField(null);
                  if (amountFrom) {
                    const n = parseFloat(amountFrom);
                    const hasDec = !Number.isNaN(n) && n % 1 !== 0;
                    setDisplayFrom(formatNumber(n, { decimals: hasDec ? 2 : 0 }));
                  }
                }}
                inputMode="decimal"
                size={1}
                className="flex-1 w-full min-w-0 bg-transparent text-[24px] sm:text-[30px] font-black focus:outline-none"
                style={{ color: brandPrimaryColor, letterSpacing: '-0.02em' }}
                placeholder={originInfo?.code === 'CLP' ? '100.000' : String(originInfo?.minTransfer || '')}
                data-testid="calc-from-input"
              />
              <Pencil
                size={16}
                className="shrink-0"
                style={{ color: `${brandPrimaryColor}99` }}
                aria-hidden="true"
              />
            </label>
            {(originInfo?.minTransfer || originInfo?.maxTransfer) && (
              <p className="text-[11px] text-gray-400 mt-1.5" data-testid="calc-limits">
                {originInfo.minTransfer ? `Min ${originInfo.minTransfer.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${originInfo.code}` : ''}
                {originInfo.minTransfer && originInfo.maxTransfer ? ' · ' : ''}
                {originInfo.maxTransfer ? `Max ${originInfo.maxTransfer.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${originInfo.code}` : ''}
              </p>
            )}
          </div>

          {/* Divisor */}
          <div className="h-px bg-gray-100 mx-5 sm:mx-6" />

          {/* ─── FILA "TU FAMILIA RECIBE" ─── */}
          <div className="px-5 sm:px-6 py-5" data-testid="calc-receive-block">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                Tu familia recibe
              </span>
              {destinationOptions.length > 1 ? (
                <div className="relative">
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="appearance-none rounded-full bg-gray-100 pl-8 pr-6 py-1 text-[11px] font-bold text-gray-700 cursor-pointer focus:outline-none"
                    data-testid="calc-country-select"
                  >
                    {destinationOptions.map((d) => (
                      <option key={d.code} value={d.code}>{d.name}</option>
                    ))}
                  </select>
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <FlagImg country={selectedDest?.flagCode || ''} size={16} />
                  </span>
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 shrink-0 rounded-full bg-gray-100 pl-2.5 pr-3 py-1">
                  <FlagImg country={selectedDest?.flagCode || ''} size={18} />
                  <span className="text-[11px] font-bold text-gray-700">{selectedDest?.code}</span>
                </div>
              )}
            </div>
            <label
              className="flex items-center gap-2 rounded-xl px-3 py-2 cursor-text transition-colors"
              style={{
                backgroundColor: '#F8F7FB',
                border: focusedField === 'to'
                  ? `1.5px solid ${brandPrimaryColor}`
                  : '1px solid #E9E7EE',
              }}
              onMouseEnter={(e) => {
                if (focusedField !== 'to') e.currentTarget.style.borderColor = `${brandPrimaryColor}55`;
              }}
              onMouseLeave={(e) => {
                if (focusedField !== 'to') e.currentTarget.style.borderColor = '#E9E7EE';
              }}
            >
              <span
                className="text-[30px] sm:text-[38px] font-black shrink-0"
                style={{ color: brandPrimaryColor }}
              >
                {selectedDest?.symbol || ''}
              </span>
              <input
                type="text"
                value={displayTo}
                onChange={(e) => handleToChange(e.target.value)}
                onFocus={(e) => { setFocusedField('to'); e.target.select(); }}
                onBlur={() => {
                  setFocusedField(null);
                  if (amountTo) {
                    const n = parseFloat(amountTo);
                    const hasDec = !Number.isNaN(n) && n % 1 !== 0;
                    setDisplayTo(formatNumber(n, { decimals: hasDec ? 2 : 0 }));
                  }
                }}
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                size={1}
                className="flex-1 w-full min-w-0 bg-transparent text-[30px] sm:text-[38px] font-black focus:outline-none placeholder-gray-300"
                style={{ color: brandPrimaryColor, letterSpacing: '-0.02em' }}
                placeholder="0"
                data-testid="calc-receive-amount"
                aria-label={`Monto a recibir en ${selectedDest?.name || ''} — editable`}
              />
              <Pencil
                size={18}
                className="shrink-0"
                style={{ color: `${brandPrimaryColor}99` }}
                aria-hidden="true"
              />
            </label>
            {adjustmentNotice && (
              <p className="text-[11px] mt-1.5 italic text-gray-500" data-testid="calc-adjustment-notice">
                {adjustmentNotice}
              </p>
            )}
          </div>
        </div>

        {/* Mensaje de tiempo de entrega — configurable en brand.landing.calculator.deliveryMessage */}
        {(BRAND?.landing?.calculator?.deliveryMessage?.textBefore || BRAND?.landing?.calculator?.deliveryMessage?.highlight) && (
          <div
            className="w-full rounded-xl bg-green-50 py-2.5 px-4 flex items-center justify-center gap-2 mb-3"
            data-testid="calc-delivery-message"
          >
            <Clock size={15} className="text-green-700 shrink-0" aria-hidden="true" />
            <p className="text-[13px] text-gray-700">
              {BRAND?.landing?.calculator?.deliveryMessage?.textBefore}
              {BRAND?.landing?.calculator?.deliveryMessage?.textBefore ? ' ' : ''}
              <strong className="uppercase tracking-wide text-green-800">
                {BRAND?.landing?.calculator?.deliveryMessage?.highlight}
              </strong>
            </p>
          </div>
        )}

        {/* Fila de detalle: tipo de cambio. La validez de la cotización con
            countdown vive en un badge fuera del bloque de detalles para no
            saturar la lectura del monto. */}
        <div className="mb-4">
          <div className="py-2 flex items-center justify-between text-[12px] text-gray-500">
            <span>Tipo de cambio</span>
            <span className="font-semibold text-gray-700">
              {loading
                ? 'Cargando…'
                : effectiveRateStr
                ? `1 ${originInfo?.code} = ${effectiveRateStr} ${selectedCountry}`
                : '—'}
            </span>
          </div>
        </div>

        {rangeValidation && (
          <p
            className={`text-xs mb-3 ${rangeValidation.level === 'error' ? 'text-red-600' : 'text-amber-600'}`}
            data-testid="calc-range-validation"
          >
            {rangeValidation.message}
          </p>
        )}

        {!hideCta && (
          <button
            onClick={handleCta}
            className="w-full text-white font-bold py-4 rounded-2xl text-base transition hover:opacity-90 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: brandPrimaryColor, boxShadow: `0 15px 40px -15px ${brandPrimaryColor}80` }}
            data-testid="calc-cta-btn"
            disabled={!!rangeValidation}
          >
            {ctaLabel || 'Comenzar a enviar'}
          </button>
        )}

        {!hideCta && (
        <p className="text-center text-xs text-gray-400 mt-4">
          Sin sucursales · Sin filas · Todo desde tu celular
        </p>
        )}
      </div>
    );
  }
  // ═══════════════════════ FIN LAYOUT SHOWCASE ═══════════════════════

  // ═══════════════════════════════════════════════════════════════════════
  // LAYOUT CLASSIC (GirosBol landing) — verdes, banderas grandes, más informal.
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div
      className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border-4"
      style={{ borderColor: brandPrimaryColor }}
      data-testid="calculator-luxury"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <h3 className="text-xl sm:text-2xl font-bold" style={{ color: brandTheme.secondary || '#111111' }}>
            Calcula tu Envío
          </h3>
        </div>
      </div>

      {/* Selector de Origen (sólo si hay más de uno) */}
      {origins.length > 1 && (
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">Enviar desde:</label>
          <div className="flex gap-2">
            {origins.map((o) => (
              <button
                key={o.code}
                onClick={() => setOriginCurrency(o.code)}
                className={`flex-1 py-3 px-4 rounded-xl border-2 flex items-center justify-center gap-2 transition ${
                  originCurrency === o.code
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`origin-${o.code.toLowerCase()}`}
              >
                <FlagImg country={o.flagCode} size={24} />
                <span className="font-semibold">{o.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Monto a enviar */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Monto a enviar ({originInfo?.code || ''}):
        </label>
        <div className="relative">
          <span
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl sm:text-2xl font-bold"
            style={{ color: brandPrimaryColor }}
          >
            {originInfo?.symbol || ''}
          </span>
          <input
            type="text"
            value={displayFrom}
            onChange={(e) => handleFromChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            onClick={(e) => e.target.select()}
            inputMode="numeric"
            className="w-full pl-12 pr-24 py-4 text-xl sm:text-2xl font-bold border-3 rounded-xl focus:outline-none focus:ring-2"
            style={{ borderColor: brandPrimaryColor }}
            placeholder={originInfo?.code === 'CLP' ? '100.000' : String(originInfo?.minTransfer || '')}
            data-testid="calc-from-input"
          />
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            <span className="font-bold text-gray-600">{originInfo?.code}</span>
            <FlagImg country={originInfo?.flagCode} size={24} />
          </div>
        </div>
        {(originInfo?.minTransfer || originInfo?.maxTransfer) && (
          <p className="text-xs text-gray-500 mt-1" data-testid="calc-limits">
            {originInfo.minTransfer ? `Min: ${originInfo.minTransfer.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${originInfo.code}` : ''}
            {originInfo.minTransfer && originInfo.maxTransfer ? ' · ' : ''}
            {originInfo.maxTransfer ? `Max: ${originInfo.maxTransfer.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} ${originInfo.code}` : ''}
          </p>
        )}
      </div>

      {/* Selector de país destino — banderas circulares */}
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2">Enviar a:</label>
        <div className="flex justify-center gap-3 flex-wrap">
          {destinationOptions.map((c) => (
            <button
              key={c.code}
              onClick={() => setSelectedCountry(c.code)}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all ${
                selectedCountry === c.code
                  ? 'ring-2 ring-green-500 ring-offset-2 scale-110'
                  : 'hover:scale-105'
              }`}
              title={c.name}
              style={{ backgroundColor: 'transparent' }}
              data-testid={`calc-dest-${c.code.toLowerCase()}`}
            >
              <FlagImg country={c.flagCode} size={selectedCountry === c.code ? 32 : 28} />
            </button>
          ))}
        </div>
        <p className="text-center text-sm text-gray-600 mt-2">{selectedDest?.name || ''}</p>
      </div>

      {/* Monto a recibir */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Destinatario recibe en {selectedDest?.name || ''}:
        </label>
        <div className="relative">
          <span
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl sm:text-2xl font-bold"
            style={{ color: '#3B82F6' }}
          >
            {selectedDest?.symbol || ''}
          </span>
          <input
            type="text"
            value={displayTo}
            onChange={(e) => handleToChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            onClick={(e) => e.target.select()}
            inputMode="numeric"
            className="w-full pl-12 pr-20 py-4 text-xl sm:text-2xl font-bold border-3 rounded-xl bg-blue-50 focus:outline-none focus:ring-2 focus:bg-white"
            style={{ borderColor: '#3B82F6' }}
            placeholder="0"
            data-testid="calc-to-input"
          />
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            <FlagImg country={selectedDest?.flagCode} size={28} />
          </div>
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">
          {loading
            ? 'Cargando tasa de cambio...'
            : effectiveRateStr
            ? `Tasa: 1 ${originInfo?.code} = ${effectiveRateStr} ${selectedCountry}`
            : ''}
        </p>
        {quote?.generatedAt && <QuoteValidityBadge quoteTimestamp={quote.generatedAt} className="mt-3" />}
      </div>

      <button
        onClick={handleCta}
        className="w-full text-white font-bold py-4 rounded-xl text-lg transition transform hover:scale-105 flex items-center justify-center gap-2"
        style={{ backgroundColor: brandPrimaryColor }}
        data-testid="calc-cta-btn"
      >
        Comenzar a Enviar
      </button>

      <p className="text-center text-xs text-gray-500 mt-4">
        Sin sucursales. Sin filas. Todo desde tu celular.
      </p>
    </div>
  );
};

export default CalculatorLuxury;
